import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { productionService } from '@services/productionService';
import { ProductionActivityFormData, PRODUCTION_ACTIVITY_CATEGORIES } from '@/types/production';

/**
 * Formulário de atividade de produção para criação e edição
 */
export const ActivityFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<ProductionActivityFormData>({
    code: '',
    name: '',
    category: 0,
    unit: '',
    estimated_minutes_per_unit: undefined,
    notes: '',
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEditing && id) {
      loadActivity(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  const loadActivity = async (activityId: string) => {
    try {
      setLoading(true);
      setError(null);

      const activity = await productionService.getActivity(parseInt(activityId));

      setFormData({
        code: activity.code,
        name: activity.name,
        category: activity.category,
        unit: activity.unit || '',
        estimated_minutes_per_unit: activity.estimated_minutes_per_unit,
        notes: activity.notes || '',
        is_active: activity.is_active !== false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar atividade');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: keyof ProductionActivityFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Código é obrigatório';
    } else if (formData.code.length > 50) {
      newErrors.code = 'Código deve ter no máximo 50 caracteres';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    } else if (formData.name.length > 150) {
      newErrors.name = 'Nome deve ter no máximo 150 caracteres';
    }

    if (formData.category < 0 || formData.category > 5) {
      newErrors.category = 'Categoria inválida';
    }

    if (
      formData.estimated_minutes_per_unit !== undefined &&
      formData.estimated_minutes_per_unit < 0
    ) {
      newErrors.estimated_minutes_per_unit = 'Tempo estimado não pode ser negativo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      const payload: ProductionActivityFormData = {
        ...formData,
        code: formData.code.trim(),
        name: formData.name.trim(),
        unit: formData.unit?.trim() || undefined,
        notes: formData.notes?.trim() || undefined,
      };

      if (isEditing && id) {
        const { code, ...updateData } = payload;
        await productionService.updateActivity(parseInt(id), updateData);
      } else {
        await productionService.createActivity(payload);
      }

      navigate('/production/activities');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar atividade';

      if (errorMessage.toLowerCase().includes('código')) {
        setErrors((prev) => ({ ...prev, code: errorMessage }));
      }

      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/production/activities')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        <Card>
          <CardContent>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/production/activities')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-h1">{isEditing ? 'Editar Atividade' : 'Nova Atividade'}</h1>
          <p className="text-slate-600">
            {isEditing
              ? 'Altere as informações da atividade de produção'
              : 'Preencha os dados para cadastrar uma nova atividade de produção'}
          </p>
        </div>
      </div>

      {/* Erro geral */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Formulário */}
      <div className="grid gap-6">
        <Card>
          <CardHeader title="Informações Básicas" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Código *"
                placeholder="Ex: PREP-01"
                value={formData.code}
                onChange={(e) => updateField('code', e.target.value.toUpperCase())}
                error={errors.code}
                maxLength={50}
                disabled={isEditing}
                helperText={isEditing ? 'O código não pode ser alterado' : undefined}
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Categoria *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => updateField('category', parseInt(e.target.value))}
                  className="input"
                >
                  {PRODUCTION_ACTIVITY_CATEGORIES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-sm text-red-600 mt-1">{errors.category}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <Input
                  label="Nome *"
                  placeholder="Ex: Flocagem de cabide adulto"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  error={errors.name}
                  maxLength={150}
                />
              </div>

              <Input
                label="Unidade"
                placeholder="Ex: UN, PC, LOTE"
                value={formData.unit || ''}
                onChange={(e) => updateField('unit', e.target.value)}
                maxLength={20}
              />

              <Input
                label="Tempo Estimado por Unidade (minutos)"
                type="number"
                placeholder="Ex: 5"
                value={formData.estimated_minutes_per_unit ?? ''}
                onChange={(e) =>
                  updateField(
                    'estimated_minutes_per_unit',
                    e.target.value === '' ? undefined : parseFloat(e.target.value)
                  )
                }
                error={errors.estimated_minutes_per_unit}
                step={0.1}
                min={0}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Observações" />
          <CardContent>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Observações / Notas
              </label>
              <textarea
                className="input w-full min-h-[100px] resize-y"
                placeholder="Informações adicionais sobre a atividade..."
                value={formData.notes || ''}
                onChange={(e) => updateField('notes', e.target.value)}
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Status" />
          <CardContent>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active !== false}
                onChange={(e) => updateField('is_active', e.target.checked)}
                className="rounded border-slate-300"
              />
              <label htmlFor="is_active" className="text-sm text-slate-700">
                Atividade ativa
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Ações */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/production/activities')}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} icon={<Save size={20} />}>
            {saving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Atividade'}
          </Button>
        </div>
      </div>
    </div>
  );
};
